import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Landmark, Plus, Pencil, Trash2, ArrowLeft, ArrowDownCircle, ArrowUpCircle,
  Building2, Star, FileText, Paperclip, Upload, Loader2, X, Zap,
  CheckCircle2, AlertTriangle, FileWarning, HelpCircle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";

function ReconBadge({ recon, size = "sm" }) {
  if (!recon) return null;
  const cls = size === "sm" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-1";
  const map = {
    reconciled: { icon: CheckCircle2, label: "Reconciled", c: "text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30" },
    off: { icon: AlertTriangle, label: `Off by ${money(Math.abs(recon.diff))}`, c: "text-amber-700 bg-amber-100 dark:bg-amber-900/30" },
    no_statement: { icon: FileWarning, label: "No statement", c: "text-slate-600 bg-slate-100 dark:bg-slate-800/60" },
    no_balance: { icon: HelpCircle, label: "Set balance", c: "text-slate-600 bg-slate-100 dark:bg-slate-800/60" },
  };
  const m = map[recon.status] || map.no_balance;
  const Icon = m.icon;
  return (
    <span data-testid={`recon-${recon.status}`} title={recon.status === "off" ? `Statement balance differs from transaction net by ${money(Math.abs(recon.diff))}` : m.label}
      className={`inline-flex items-center gap-1 rounded-full font-semibold ${cls} ${m.c}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export default function BankAccounts({ refs, canWrite }) {
  const [accounts, setAccounts] = useState([]);
  const [editing, setEditing] = useState(null); // {} for new
  const [selected, setSelected] = useState(null); // acctId for profile

  const load = useCallback(() => {
    api.get("/bank/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (selected) return <AccountProfile acctId={selected} refs={refs} canWrite={canWrite} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="space-y-4" data-testid="bank-accounts-view">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Register each bank account, then click a card to see its full profile — transactions, Zelle, statements, notes and documents.</p>
        {canWrite && <Button className="rounded-xl" onClick={() => setEditing({})} data-testid="add-account-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Account</Button>}
      </div>

      {accounts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="no-accounts">
          <Landmark className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No bank accounts yet. Add one to start rolling up transactions and Zelle payments.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map((a) => (
            <div key={a.id} onClick={() => setSelected(a.id)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") setSelected(a.id); }}
              data-testid={`account-card-${a.id}`}
              className="rounded-2xl border border-border bg-card p-5 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Landmark className="w-5 h-5" /></div>
                <div className="flex items-center gap-1.5">
                  {a.is_default_zelle && <span title="Default Zelle deposit account" className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 bg-violet-100 dark:bg-violet-900/30 px-1.5 py-0.5 rounded-full"><Zap className="w-3 h-3" /> Zelle</span>}
                  {canWrite && <button onClick={(e) => { e.stopPropagation(); setEditing(a); }} data-testid={`edit-account-${a.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>}
                </div>
              </div>
              <p className="font-heading font-bold mt-3">{a.bank_name || "Bank"}</p>
              <p className="text-sm text-muted-foreground">{a.account_name || "Account"}{a.last4 ? ` ····${a.last4}` : ""} · {a.account_type}</p>
              {a.property_name && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> {a.property_name}</p>}
              <div className="mt-2"><ReconBadge recon={a.recon} /></div>
              <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-border text-center">
                <div><p className="text-[10px] uppercase text-muted-foreground">Txns</p><p className="font-semibold text-sm">{a.txn_count}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">In</p><p className="font-semibold text-sm text-emerald-600">{money(a.inflow)}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">Out</p><p className="font-semibold text-sm text-red-500">{money(a.outflow)}</p></div>
              </div>
              {a.current_balance != null && <p className="text-xs text-muted-foreground mt-2">Statement balance: <b className="text-foreground">{money(a.current_balance)}</b></p>}
            </div>
          ))}
        </div>
      )}

      {editing !== null && <AccountDialog acct={editing} refs={refs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AccountDialog({ acct, refs, onClose, onSaved }) {
  const isNew = !acct.id;
  const [f, setF] = useState({
    bank_name: acct.bank_name || "", account_name: acct.account_name || "", last4: acct.last4 || "",
    account_type: acct.account_type || "Checking", property_id: acct.property_id || "",
    notes: acct.notes || "", current_balance: acct.current_balance ?? "", is_default_zelle: !!acct.is_default_zelle,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.bank_name.trim() && !f.account_name.trim()) { toast.error("Enter a bank or account name"); return; }
    setSaving(true);
    try {
      const prop = refs.properties.find((p) => p.id === f.property_id);
      const payload = { ...f, property_name: prop?.name || "", current_balance: f.current_balance === "" ? null : Number(f.current_balance) };
      const res = isNew ? await api.post("/bank/accounts", payload) : await api.put(`/bank/accounts/${acct.id}`, payload);
      const linked = res.data?.autolinked || 0;
      toast.success(`Account saved${linked ? ` · ${linked} existing transaction(s) linked` : ""}`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const fld = "w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";
  const lbl = "text-xs font-semibold text-muted-foreground uppercase tracking-wider";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="account-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add Bank Account" : "Edit Bank Account"}</DialogTitle>
          <DialogDescription>Transactions whose text matches this account name or last-4 are linked automatically.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><label className={lbl}>Bank Name</label><input value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="acct-bank-name" className={fld} placeholder="Wells Fargo" /></div>
          <div><label className={lbl}>Account Name</label><input value={f.account_name} onChange={(e) => set("account_name", e.target.value)} data-testid="acct-account-name" className={fld} placeholder="Operating" /></div>
          <div><label className={lbl}>Last 4 Digits</label><input value={f.last4} onChange={(e) => set("last4", e.target.value)} data-testid="acct-last4" className={fld} placeholder="1234" maxLength={4} /></div>
          <div><label className={lbl}>Account Type</label>
            <select value={f.account_type} onChange={(e) => set("account_type", e.target.value)} data-testid="acct-type" className={fld}>
              {["Checking", "Savings", "Money Market", "Business", "Other"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Linked Property</label>
            <select value={f.property_id} onChange={(e) => set("property_id", e.target.value)} data-testid="acct-property" className={fld}>
              <option value="">— None —</option>
              {refs.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Statement Balance</label><input type="number" value={f.current_balance} onChange={(e) => set("current_balance", e.target.value)} data-testid="acct-balance" className={fld} placeholder="0.00" /></div>
          <div className="col-span-2"><label className={lbl}>Notes</label><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="acct-notes" className={fld} /></div>
          <label className="col-span-2 flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={f.is_default_zelle} onChange={(e) => set("is_default_zelle", e.target.checked)} data-testid="acct-default-zelle" />
            <Zap className="w-4 h-4 text-violet-500" /> Default Zelle deposit account (all Zelle payments roll up here)
          </label>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="acct-save">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountProfile({ acctId, refs, canWrite, onBack }) {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ from: "", to: "", property: "", tenant: "", source: "", type: "" });
  const [txnDetail, setTxnDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/bank/accounts/${acctId}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load account"));
  }, [acctId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.transactions.filter((t) => {
      if (filters.from && t.date < filters.from) return false;
      if (filters.to && t.date > filters.to) return false;
      if (filters.property && t.property_name !== filters.property) return false;
      if (filters.tenant && !(t.tenant_name || "").toLowerCase().includes(filters.tenant.toLowerCase())) return false;
      if (filters.source && t.source !== filters.source) return false;
      if (filters.type && t.direction !== filters.type) return false;
      return true;
    });
  }, [data, filters]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("File too large (max 6MB)."); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      await api.post(`/bank/accounts/${acctId}/attach`, { name: file.name, data: dataUrl });
      toast.success("Document attached"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const detachDoc = async (docId) => {
    try { await api.delete(`/bank/accounts/${acctId}/attach/${docId}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const viewDoc = async (docId) => {
    try { const { data: d } = await api.get(`/documents/${docId}`); const w = window.open(); if (w) w.document.write(`<iframe src="${d.data}" style="width:100%;height:100%;border:0"></iframe>`); }
    catch { toast.error("Could not open document"); }
  };

  if (!data) return <div className="py-12 text-center text-muted-foreground">Loading account…</div>;
  const a = data.account;
  const t = data.totals;

  return (
    <div className="space-y-5" data-testid="account-profile">
      <button onClick={onBack} data-testid="account-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back to accounts</button>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-heading text-2xl font-bold flex items-center gap-2"><Landmark className="w-6 h-6 text-primary" /> {a.bank_name || "Bank"} {a.is_default_zelle && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 bg-violet-100 dark:bg-violet-900/30 px-1.5 py-0.5 rounded-full"><Zap className="w-3 h-3" /> Zelle</span>}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{a.account_name}{a.last4 ? ` ····${a.last4}` : ""} · {a.account_type}{a.property_name ? ` · ${a.property_name}` : ""}</p>
            {a.notes && <p className="text-sm mt-2">{a.notes}</p>}
          </div>
          {a.current_balance != null && <div className="text-right"><p className="text-[11px] uppercase text-muted-foreground">Statement Balance</p><p className="font-heading text-2xl font-bold">{money(a.current_balance)}</p><div className="mt-1 flex justify-end"><ReconBadge recon={data.recon} size="md" /></div></div>}
        </div>
        {a.current_balance == null && <div className="mt-2"><ReconBadge recon={data.recon} size="md" /></div>}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[["Transactions", t.count, ""], ["Inflow", money(t.inflow), "text-emerald-600"], ["Outflow", money(t.outflow), "text-red-500"], ["Net", money(t.net), t.net >= 0 ? "text-emerald-600" : "text-red-500"]].map(([l, v, c]) => (
            <div key={l} className="rounded-xl border border-border p-3"><p className="text-[10px] uppercase text-muted-foreground font-semibold">{l}</p><p className={`font-heading text-lg font-bold ${c}`}>{v}</p></div>
          ))}
        </div>
      </div>

      {/* documents + statements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold flex items-center gap-2"><Paperclip className="w-4 h-4 text-primary" /> Documents</p>
            {canWrite && <><input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="acct-doc-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="acct-attach-btn" className="text-xs inline-flex items-center gap-1 text-primary hover:underline">{uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Attach</button></>}
          </div>
          {(a.attachments || []).length === 0 ? <p className="text-xs text-muted-foreground">No documents attached.</p> : (
            <div className="space-y-1.5">{(a.attachments || []).map((d) => (
              <div key={d.doc_id} className="flex items-center justify-between text-sm" data-testid={`acct-doc-${d.doc_id}`}>
                <button onClick={() => viewDoc(d.doc_id)} className="flex items-center gap-1.5 min-w-0 hover:text-primary"><FileText className="w-4 h-4 shrink-0" /><span className="truncate">{d.name}</span></button>
                {canWrite && <button onClick={() => detachDoc(d.doc_id)} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}</div>
          )}
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-sm font-semibold flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-primary" /> Statement Uploads & Reconciliation</p>
          {data.statements.length === 0 ? <p className="text-xs text-muted-foreground">No statements uploaded for this account.</p> : (
            <div className="space-y-2">{data.statements.map((s) => (
              <StatementRow key={s.id} stmt={s} canWrite={canWrite} onSaved={load} />
            ))}</div>
          )}
          {data.monthly.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-[11px] uppercase text-muted-foreground font-semibold mb-1">Monthly totals</p>
              {data.monthly.slice(0, 4).map((m) => (
                <div key={m.month} className="flex items-center justify-between text-xs py-0.5">
                  <span>{m.month}</span><span className="text-emerald-600">+{money(m.inflow)}</span><span className="text-red-500">−{money(m.outflow)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* filters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <input type="date" value={filters.from} onChange={(e) => setFilters((s) => ({ ...s, from: e.target.value }))} data-testid="filter-from" className="rounded-lg border border-border bg-background p-2 text-xs" />
        <input type="date" value={filters.to} onChange={(e) => setFilters((s) => ({ ...s, to: e.target.value }))} data-testid="filter-to" className="rounded-lg border border-border bg-background p-2 text-xs" />
        <select value={filters.property} onChange={(e) => setFilters((s) => ({ ...s, property: e.target.value }))} data-testid="filter-property" className="rounded-lg border border-border bg-background p-2 text-xs">
          <option value="">All properties</option>{refs.properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
        </select>
        <input value={filters.tenant} onChange={(e) => setFilters((s) => ({ ...s, tenant: e.target.value }))} placeholder="Tenant" data-testid="filter-tenant" className="rounded-lg border border-border bg-background p-2 text-xs" />
        <select value={filters.source} onChange={(e) => setFilters((s) => ({ ...s, source: e.target.value }))} data-testid="filter-source" className="rounded-lg border border-border bg-background p-2 text-xs">
          <option value="">All sources</option><option>Bank Import</option><option>Zelle</option>
        </select>
        <select value={filters.type} onChange={(e) => setFilters((s) => ({ ...s, type: e.target.value }))} data-testid="filter-type" className="rounded-lg border border-border bg-background p-2 text-xs">
          <option value="">All types</option><option value="income">Income</option><option value="expense">Expense</option>
        </select>
      </div>

      {/* combined transactions */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground"><tr>
              <th className="text-left font-semibold px-4 py-3">Date</th><th className="text-left font-semibold px-4 py-3">Description</th>
              <th className="text-right font-semibold px-4 py-3">Amount</th><th className="text-left font-semibold px-4 py-3">Category</th>
              <th className="text-left font-semibold px-4 py-3">Property / Tenant</th><th className="text-left font-semibold px-4 py-3">Source</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No transactions match these filters.</td></tr>}
              {filtered.map((x) => (
                <tr key={`${x.kind}-${x.id}`} onClick={() => setTxnDetail(x)} data-testid={`acct-txn-${x.id}`} className="border-t border-border hover:bg-muted/40 cursor-pointer">
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{x.date}</td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1.5">{x.direction === "income" ? <ArrowDownCircle className="w-4 h-4 text-emerald-500 shrink-0" /> : <ArrowUpCircle className="w-4 h-4 text-red-400 shrink-0" />}<span className="truncate max-w-[240px]">{x.description}</span></span></td>
                  <td className={`px-4 py-3 text-right font-semibold ${x.direction === "income" ? "text-emerald-600" : ""}`}>{x.direction === "income" ? "+" : "−"}{money(x.amount)}</td>
                  <td className="px-4 py-3">{x.category || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{[x.property_name, x.tenant_name].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${x.source === "Zelle" ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30" : "bg-muted text-muted-foreground"}`}>{x.source}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {txnDetail && <TxnDetail txn={txnDetail} canWrite={canWrite} onClose={() => setTxnDetail(null)} onSaved={() => { setTxnDetail(null); load(); }} />}
    </div>
  );
}

function StatementRow({ stmt, canWrite, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sb, setSb] = useState(stmt.starting_balance ?? "");
  const [eb, setEb] = useState(stmt.ending_balance ?? stmt.current_balance ?? "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/bank/statements/${stmt.id}/reconcile`, {
        starting_balance: sb === "" ? null : Number(sb), ending_balance: eb === "" ? null : Number(eb),
      });
      toast[data.recon.status === "off" ? "warning" : "success"](
        data.recon.status === "reconciled" ? "Statement reconciled ✓" :
        data.recon.status === "off" ? `Off by ${money(Math.abs(data.recon.diff))} — check this statement` : "Saved");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border border-border p-2.5" data-testid={`stmt-row-${stmt.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate flex items-center gap-1.5 text-sm min-w-0"><FileText className="w-4 h-4 text-muted-foreground shrink-0" /> <span className="truncate">{stmt.filename}</span></span>
        <div className="flex items-center gap-2 shrink-0">
          <ReconBadge recon={stmt.recon} />
          {canWrite && <button onClick={() => setOpen((o) => !o)} data-testid={`stmt-recon-toggle-${stmt.id}`} className="text-xs text-primary hover:underline">{open ? "Close" : "Reconcile"}</button>}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-0.5">{stmt.period_start ? `${stmt.period_start} → ${stmt.period_end} · ` : ""}{stmt.transaction_count} txns · net {money(stmt.net || 0)}</p>
      {(stmt.gap || stmt.chain_break) && (
        <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1" data-testid={`stmt-warn-${stmt.id}`}>
          <AlertTriangle className="w-3 h-3" /> {stmt.gap ? "Possible missing statement (gap in periods). " : ""}{stmt.chain_break ? "Starting balance doesn't match prior ending." : ""}
        </p>
      )}
      {open && canWrite && (
        <div className="mt-2 pt-2 border-t border-border grid grid-cols-3 gap-2 items-end">
          <div><label className="text-[10px] uppercase text-muted-foreground font-semibold">Starting{stmt.starting_balance != null ? " (carried)" : ""}</label><input type="number" value={sb} onChange={(e) => setSb(e.target.value)} data-testid={`stmt-start-${stmt.id}`} className="w-full mt-0.5 rounded-lg border border-border bg-background p-1.5 text-xs" /></div>
          <div><label className="text-[10px] uppercase text-muted-foreground font-semibold">Ending</label><input type="number" value={eb} onChange={(e) => setEb(e.target.value)} data-testid={`stmt-end-${stmt.id}`} className="w-full mt-0.5 rounded-lg border border-border bg-background p-1.5 text-xs" /></div>
          <Button className="rounded-lg h-8 text-xs" onClick={save} disabled={saving} data-testid={`stmt-recon-save-${stmt.id}`}>{saving ? "…" : "Check"}</Button>
          <p className="col-span-3 text-[11px] text-muted-foreground">Expected ending = starting + net ({money(stmt.net || 0)}) = <b className="text-foreground">{money((Number(sb) || 0) + (stmt.net || 0))}</b>. Ending auto-carries to the next statement's starting balance.</p>
        </div>
      )}
    </div>
  );
}

function TxnDetail({ txn, canWrite, onClose, onSaved }) {
  const [note, setNote] = useState(txn.notes || "");
  const [saving, setSaving] = useState(false);
  const canEdit = canWrite && txn.kind === "bank";
  const save = async () => {
    setSaving(true);
    try { await api.put(`/bank/transactions/${txn.id}/notes`, { notes: note }); toast.success("Note saved"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="acct-txn-detail">
        <DialogHeader><DialogTitle>{txn.direction === "income" ? "+" : "−"}{money(txn.amount)}</DialogTitle>
          <DialogDescription>{txn.source} · {txn.date}</DialogDescription></DialogHeader>
        <div className="space-y-2 text-sm">
          <p><span className="text-muted-foreground">Description:</span> {txn.description}</p>
          <p><span className="text-muted-foreground">Category:</span> {txn.category || "—"}</p>
          <p><span className="text-muted-foreground">Property / Tenant:</span> {[txn.property_name, txn.tenant_name].filter(Boolean).join(" · ") || "—"}</p>
          <p><span className="text-muted-foreground">Status:</span> {txn.status}</p>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Notes</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} disabled={!canEdit} data-testid="acct-txn-note"
              className="w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm disabled:opacity-60" placeholder={canEdit ? "Add a note…" : "Notes editable on imported bank transactions only"} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={onClose}><X className="w-4 h-4 mr-1" /> Close</Button>
          {canEdit && <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="acct-txn-save">{saving ? "Saving…" : "Save Note"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
